import React, { Fragment, useMemo, useState } from 'react';
import {
  AvatarGroup,
  Box,
  Button,
  IconButton,
  Menu,
  Stack,
} from '@mui/material';
import { CheckFilter } from '../../../../../components/data-table/filters';
import { useGetAgentsList } from '../../../../../reactQuery/Agents/agents';
import { ICheckboxOption } from '../../../../../components/forms/check/CheckboxOption/interfaces';
import { capitalizeStr } from '../../../../../helpers/stringHelpers';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IFilterOnAgentsButton } from './interfaces';
import { useTranslation } from 'react-i18next';
import { AgentAvatar } from '../../../../../components/AgentAvatar';
import { Agent } from '../../../../../api/agentsApi/interfaces';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

export const FilterOnAgentsButton: React.FC<IFilterOnAgentsButton> = ({
  selectedAgents,
  onChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    data: agentsList,
    isLoading: loadingAgentsList,
    error: agentsListError,
  } = useGetAgentsList();

  const selectedAgentObjects = useMemo<Agent[]>(() => {
    if (!agentsList || !selectedAgents.length) {
      return [];
    }

    return selectedAgents
      .map((id) => agentsList.find((agent) => agent.id === id))
      .filter((agent): agent is Agent => Boolean(agent));
  }, [agentsList, selectedAgents]);

  return (
    <Fragment>
      <Stack direction="row" alignItems="center" gap="5px">
        {selectedAgentObjects.length > 0 && (
          <AvatarGroup
            key={selectedAgentObjects.length}
            sx={{
              '& .MuiAvatarGroup-avatar': {
                width: 25,
                height: 25,
                fontSize: 14,
              },
            }}
            max={8}
          >
            {selectedAgentObjects.map((agent) => {
              return <AgentAvatar agent={agent} variant="pill" />;
            })}
          </AvatarGroup>
        )}

        {selectedAgents.length > 0 && (
          <IconButton size="small" onClick={() => onChange([])}>
            <FilterAltOffIcon
              sx={{
                width: 20,
                height: 20,
              }}
            />
          </IconButton>
        )}
        <Button
          onClick={handleClick}
          variant="contained"
          size="small"
          endIcon={<ArrowDropDownIcon />}
        >
          {`${t('opportunities:dashboard.buttons.filterOnAgentsBtn')} ${selectedAgents.length > 0 ? `(${selectedAgents.length})` : ''}`}
        </Button>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box
          sx={{
            padding: '5px 10px',
            minWidth: '150px',
          }}
        >
          <CheckFilter
            label={'Select agents'}
            value={selectedAgents as Array<string | number>}
            onChange={(val) => onChange(val as number[])}
            options={
              agentsList?.map(
                (agent): ICheckboxOption => ({
                  label: capitalizeStr(`${agent.firstName} ${agent.lastName}`),
                  value: agent.id,
                })
              ) || []
            }
          />
        </Box>
      </Menu>
    </Fragment>
  );
};
